import { tx as translateInterfaceText } from '@/utils/i18n';
import { useLanguage } from '@/hooks/useLanguage';
import { useMemo, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { GitFork } from 'lucide-react';
import { startOfDay } from 'date-fns';
import { toast } from 'sonner';
import { Layout } from '@/components/layout/Layout';
import { Button } from '@/components/ui/button';
import { PeriodPicker } from '@/components/ui/period-picker';
import { Skeleton } from '@/components/ui/skeleton';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { FlowStatusBadge } from '@/components/vsl-dinamica/shared/FlowStatusBadge';
import { ChoicesTable } from '@/components/vsl-dinamica/metrics/ChoicesTable';
import { ExitsTable } from '@/components/vsl-dinamica/metrics/ExitsTable';
import { MetricsKpis } from '@/components/vsl-dinamica/metrics/MetricsKpis';
import { StepsTable } from '@/components/vsl-dinamica/metrics/StepsTable';
import { TrackingDialog } from '@/components/vsl-dinamica/metrics/TrackingDialog';
import { VideosMetrics } from '@/components/vsl-dinamica/metrics/VideosMetrics';
import { MAX_RANGE_DAYS, isRangeAllowed, toApiDate } from '@/components/vsl-dinamica/metrics/metricsUtils';
import { useFlow, useFlowMetrics } from '@/hooks/vsl-dinamica/useDynamicFlows';
import { buildPresetRange, type PeriodPreset } from '@/utils/period-presets';

// O BE aceita no máximo 90 dias; o atalho de 12 meses fica de fora.
const PRESETS: PeriodPreset[] = ['last7Days', 'last30Days', 'last90Days'];

export default function FlowMetricsPage() {
  useLanguage();
  const [trackingOpen, setTrackingOpen] = useState(false);
  const { flowId } = useParams<{ flowId: string }>();
  const [range, setRange] = useState(() => buildPresetRange('last30Days', startOfDay(new Date())));
  const apiRange = useMemo(() => ({ from: toApiDate(range.from), to: toApiDate(range.to) }), [range]);

  const flowQuery = useFlow(flowId);
  const metricsQuery = useFlowMetrics(flowId, apiRange);
  const flow = flowQuery.data;
  const metrics = metricsQuery.data;

  const changeRange = (next: { from: Date; to: Date }) => {
    if (!isRangeAllowed(next)) {
      toast.error(translateInterfaceText("Escolha um período de até {value0} dias.", { value0: (MAX_RANGE_DAYS) }));
      return;
    }
    setRange(next);
  };

  const notFound = flowQuery.isError && (flowQuery.error as { status?: number })?.status === 404;
  const noData = !!metrics && metrics.totals.views === 0 && metrics.totals.plays === 0;

  return (
    <Layout fixedSidebar>
      <div className="flex flex-1 flex-col overflow-hidden">
        {/* Cabeçalho no padrão das telas de recurso (lista da VSL Dinâmica, Hook Track): ícone, título e separador. */}
        <div className="flex-shrink-0 bg-transparent">
          <div className="mx-auto w-full max-w-[1600px] px-4 pb-4 pt-4 md:px-8 md:pb-5" data-page-header>
            <div className="flex items-center gap-2.5">
              <GitFork className="h-[18px] w-[18px] text-[#2D6CF6]" strokeWidth={2} aria-hidden="true" />
              <h1 className="text-[20px] font-semibold leading-[1.2] tracking-tight text-[#1E2330] md:text-[22px]">
                {translateInterfaceText("Métricas do fluxo")}</h1>
            </div>
          </div>
          <div
            data-testid="page-header-separator"
            className="hidden h-px w-full lg:block"
            style={{ backgroundColor: 'rgba(0, 0, 0, 0.08)' }}
          />

          {/* Trilha (como nas métricas do Hook Track) e ações da página. */}
          <div className="mx-auto flex w-full max-w-[1600px] flex-wrap items-center justify-between gap-3 px-4 pb-2 pt-4 md:px-8">
            <div className="flex min-w-0 flex-wrap items-center gap-2">
              <nav aria-label={translateInterfaceText("Trilha")} className="flex min-w-0 items-center gap-2 text-[14px]">
                <Link to="/vsl-dinamica" className="font-medium text-[#404040] hover:underline">
                  {translateInterfaceText("VSL Dinâmica")}</Link>
                <span aria-hidden="true" className="text-[#9E9E9E]">
                  {">"}</span>
                <span className="truncate text-[#9E9E9E]">{flow?.name || '…'}</span>
              </nav>
              {flow && <FlowStatusBadge status={flow.status} version={flow.published_version} />}
            </div>
            <div className="flex flex-wrap gap-2">
              <PeriodPicker value={range} onChange={changeRange} presets={PRESETS} className="sm:w-[240px]" />
              {flowId && (
                <Button asChild className="h-[42px] font-semibold">
                  <Link to={`/vsl-dinamica/${flowId}`}>{translateInterfaceText("Editar fluxo")}</Link>
                </Button>
              )}
            </div>
          </div>
        </div>

        <main className="flex-1 overflow-y-auto">
          <div className="mx-auto flex w-full max-w-[1600px] flex-col gap-5 px-4 pb-10 pt-7 md:px-8">
            {notFound ? (
              <section className="flex flex-col items-center gap-3 rounded-2xl border border-[#D9E0EA] bg-white px-6 py-12 text-center">
                <p className="text-sm text-[#4B5563]">{translateInterfaceText("Este fluxo não existe ou foi excluído.")}</p>
                <Button asChild variant="outline" className="rounded-xl">
                  <Link to="/vsl-dinamica">{translateInterfaceText("Voltar para a lista")}</Link>
                </Button>
              </section>
            ) : metricsQuery.isLoading ? (
              <div className="flex flex-col gap-4" aria-busy="true">
                <div className="grid grid-cols-[repeat(auto-fit,minmax(min(100%,190px),1fr))] gap-3.5">
                  {[0, 1, 2, 3, 4].map((key) => (
                    <Skeleton key={key} className="h-[110px] rounded-2xl" />
                  ))}
                </div>
                <Skeleton className="h-[260px] rounded-2xl" />
                <Skeleton className="h-[220px] rounded-2xl" />
              </div>
            ) : metricsQuery.isError || !metrics ? (
              <section className="flex flex-col items-center gap-3 rounded-2xl border border-[#D9E0EA] bg-white px-6 py-12 text-center">
                <p className="text-sm text-[#4B5563]">
                  {(metricsQuery.error as Error)?.message || translateInterfaceText("Não foi possível carregar as métricas.")}
                </p>
                <Button
                  variant="outline"
                  className="rounded-xl"
                  disabled={metricsQuery.isRefetching}
                  onClick={() => metricsQuery.refetch()}
                >
                  {translateInterfaceText("Tentar novamente")}</Button>
              </section>
            ) : (
              <Tabs defaultValue="fluxo" className="flex flex-col gap-4">
                <TabsList className="w-fit">
                  <TabsTrigger className="data-[state=active]:bg-[#0057FF] data-[state=active]:text-white" value="fluxo">{translateInterfaceText("Fluxo")}</TabsTrigger>
                  <TabsTrigger className="data-[state=active]:bg-[#0057FF] data-[state=active]:text-white" value="videos">{translateInterfaceText("Vídeos")}</TabsTrigger>
                </TabsList>
                <div className="flex flex-wrap items-center justify-between gap-3 rounded-xl border border-[#BFD3FF] bg-[#F5F8FF] p-4">
                  <div><p className="text-sm font-semibold text-[#111827]">{translateInterfaceText("Rastreamento de vendas")}</p><p className="text-xs text-[#4B5563]">{metrics.sales?.connected ? translateInterfaceText("Integração vinculada. Vendas aprovadas são recebidas pelo webhook configurado na plataforma.") : translateInterfaceText("Conecte sua plataforma para acompanhar vendas e receita. Acesso ao checkout não confirma compra.")}</p></div>
                  <Button className="shrink-0 bg-[#0057FF] hover:bg-[#0046CC]" onClick={() => setTrackingOpen(true)}>{metrics.sales?.connected ? translateInterfaceText("Gerenciar rastreamento") : translateInterfaceText("Conectar rastreamento")}</Button>
                </div>
                {noData && (
                  <p className="rounded-xl border border-[#D9E0EA] bg-white px-4 py-3 text-sm text-[#4B5563]">
                    {translateInterfaceText("Ainda não há visitas neste período. As métricas aparecem quando o fluxo publicado recebe visitas.")}</p>
                )}
                <TabsContent value="fluxo" className="mt-0 flex flex-col gap-4">
                  <MetricsKpis totals={metrics.totals} sales={metrics.sales} />
                  <div className="flex flex-wrap gap-4">
                    <StepsTable steps={metrics.steps} />
                    <ExitsTable exits={metrics.exits} />
                  </div>
                  <ChoicesTable choices={metrics.choices} />
                </TabsContent>
                <TabsContent value="videos" className="mt-0">
                  <VideosMetrics videos={metrics.videos ?? []} />
                </TabsContent>
              </Tabs>
            )}
          </div>
        </main>
        {flowId && trackingOpen && <TrackingDialog flowId={flowId} exits={flow?.draft?.graph?.exits || []} open={trackingOpen} onOpenChange={setTrackingOpen} />}
      </div>
    </Layout>
  );
}
